import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
import { PageHero } from '../components/PageHero';
import { CTABand } from '../components/CTABand';
import { SectionLabel } from '../components/SectionLabel';
import { PropFlowPreview } from '../components/PropFlowPreview';
import { FAQS, IMAGES, PROPFLOW_METRICS, PROPFLOW_MODULES, PROPFLOW_WORKFLOWS } from '../data/site';
export function PropFlow() {
  return <>
      <PageHero eyebrow="PropFlow · Live" title="Your whole portfolio on one platform, chasing its own follow-ups." lede="A property management system with AI workflows built in. Rent reminders, arrears alerts, and maintenance requests run automatically, so growing the portfolio does not mean growing the risk of losing information." aside={<Link to="/contact" className="group inline-flex items-center gap-2 bg-bronze-500 px-6 py-3.5 text-sm font-medium text-white transition-colors duration-150 ease-out hover:bg-bronze-600" data-id="element-394" data-mp="3rdmea39w9pyz">
            Book a demo
            <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-out group-hover:translate-x-1" aria-hidden="true" data-id="element-395" data-mp="1vci1313rndz7" />
          </Link>} data-id="element-393" data-mp="e4holgna3lmu" />

      {/* Problem → platform */}
      <section className="border-b border-ink-100" data-id="element-396" data-mp="24d6kkbbxi6jh">
        <div className="mx-auto grid max-w-container gap-12 px-6 py-16 lg:grid-cols-[1fr_1fr] lg:items-center lg:gap-20 lg:px-10 lg:py-24" data-id="element-397" data-mp="1gm135rqcb672">
          <div data-id="element-398" data-mp="1cn1zeagchfh7">
            <SectionLabel data-id="element-399" data-mp="1cje53932sp1f">The problem</SectionLabel>
            <h2 className="mt-6 text-3xl font-semibold leading-tight tracking-tight text-ink-900 lg:text-4xl" data-id="element-400" data-mp="1qdnk84pczzhq">
              Ten units is a notebook. Sixty is a liability.
            </h2>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-ink-600" data-id="element-401" data-mp="xvjb7pdc0emr">
              Most portfolios grow faster than the system holding them. Rent
              reminders depend on someone remembering. A maintenance complaint
              arrives at 9pm in a chat and is gone by morning. Payments sit in a
              mobile money statement that nobody has matched to an invoice in
              three weeks.
            </p>
            <p className="mt-4 max-w-lg text-base leading-relaxed text-ink-600" data-id="element-402" data-mp="32q2jbgbjcnj2">
              PropFlow closes those gaps by making the follow-up automatic and
              the record complete — one place where every unit, tenant, payment,
              and repair is visible at once.
            </p>
          </div>
          <PropFlowPreview data-id="element-403" data-mp="2f1jcl584wit3" />
        </div>
      </section>

      {/* AI workflows — the core */}
      <section className="border-b border-ink-100 bg-ink-950" data-id="element-404" data-mp="2k15q7frvk12f">
        <div className="mx-auto max-w-container px-6 py-16 lg:px-10 lg:py-24" data-id="element-405" data-mp="2o162atri406b">
          <div className="max-w-2xl" data-id="element-406" data-mp="3vepyqv9lsiop">
            <SectionLabel tone="dark" data-id="element-407" data-mp="3vdoq0xg288a5">AI workflows</SectionLabel>
            <h2 className="mt-6 text-3xl font-semibold leading-tight tracking-tight text-white lg:text-4xl" data-id="element-408" data-mp="394bck7q0y8h">
              The work that used to depend on someone remembering
            </h2>
            <p className="mt-5 text-base leading-relaxed text-ink-400" data-id="element-409" data-mp="1jqcsjo7wzy4i">
              Each workflow runs on its own trigger, writes back to the record,
              and escalates to a human only when a decision is actually needed.
            </p>
          </div>

          <ul className="mt-12 grid gap-px bg-ink-800 md:grid-cols-2" data-id="element-410" data-mp="151ozpppge2i9">
            {PROPFLOW_WORKFLOWS.map(workflow => <li key={workflow.name} className="bg-ink-950 p-8 lg:p-10" data-id="element-411" data-mp="1023zzd2glq4h">
                <span className="text-xs font-medium uppercase tracking-[0.14em] text-bronze-300" data-id="element-412" data-mp="w10latw5e04u">
                  {workflow.trigger}
                </span>
                <h3 className="mt-4 text-xl font-semibold tracking-tight text-white" data-id="element-413" data-mp="r3i8utix92s3">
                  {workflow.name}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-ink-400" data-id="element-414" data-mp="2lfynahlvfn81">
                  {workflow.text}
                </p>
              </li>)}
          </ul>
        </div>
      </section>

      {/* Modules */}
      <section className="border-b border-ink-100" data-id="element-415" data-mp="1n4dy30z3uodt">
        <div className="mx-auto max-w-container px-6 py-16 lg:px-10 lg:py-24" data-id="element-416" data-mp="1n21giox9eg1a">
          <SectionLabel data-id="element-417" data-mp="193upnsqq9gb4">Inside the platform</SectionLabel>
          <h2 className="mt-6 max-w-2xl text-3xl font-semibold leading-tight tracking-tight text-ink-900 lg:text-4xl" data-id="element-418" data-mp="1e2xwniq9pwlq">
            Everything in one place, monitored from one screen
          </h2>

          <ul className="mt-12 grid gap-x-12 gap-y-9 sm:grid-cols-2 lg:grid-cols-3" data-id="element-419" data-mp="145tb9l48y55z">
            {PROPFLOW_MODULES.map(module => <li key={module.title} className="border-t border-ink-200 pt-5" data-id="element-420" data-mp="qfg2jgjj6mfl">
                <h3 className="text-lg font-semibold tracking-tight text-ink-900" data-id="element-421" data-mp="ugi4i54u1cl9">
                  {module.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-ink-600" data-id="element-422" data-mp="ghs6mna6v8vu">
                  {module.text}
                </p>
              </li>)}
          </ul>
        </div>
      </section>

      {/* Outcome band */}
      <section className="border-b border-ink-100 bg-ink-50" data-id="element-423" data-mp="3szb6v1ecggwr">
        <div className="mx-auto grid max-w-container gap-10 px-6 py-16 lg:grid-cols-[1fr_1fr] lg:items-center lg:gap-16 lg:px-10 lg:py-20" data-id="element-424" data-mp="3t14ccdd80v36">
          <img src={IMAGES.propflow} alt="Residential apartment blocks of the kind managed in PropFlow" className="aspect-[4/3] w-full object-cover" data-id="element-425" data-mp="6scsnq07gn80" />
          <div data-id="element-426" data-mp="ft188p58yvn0">
            <SectionLabel data-id="element-427" data-mp="ftje8dhpoo84">What changes</SectionLabel>
            <h2 className="mt-6 text-2xl font-semibold leading-tight tracking-tight text-ink-900 lg:text-3xl" data-id="element-428" data-mp="fqot0jqvizdn">
              Scale the portfolio, not the admin
            </h2>
            <dl className="mt-8 space-y-6" data-id="element-429" data-mp="pj2zfpswcu65">
              {PROPFLOW_METRICS.map(metric => <div key={metric.label} className="flex items-baseline gap-5" data-id="element-430" data-mp="13ivkp5lnmi6q">
                  <dt className="sr-only" data-id="element-431" data-mp="zhs6enj98u7m">{metric.label}</dt>
                  <dd className="flex items-baseline gap-5" data-id="element-432" data-mp="16jys16u23vuc">
                    <span className="w-24 shrink-0 text-3xl font-semibold tracking-tight text-bronze-600" data-id="element-433" data-mp="16li8oyjmk8wn">
                      {metric.value}
                    </span>
                    <span className="text-sm leading-snug text-ink-600" data-id="element-434" data-mp="1ghv2dwf66mre">
                      {metric.label}
                    </span>
                  </dd>
                </div>)}
            </dl>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b border-ink-100" data-id="element-435" data-mp="1u99km1196jxe">
        <div className="mx-auto grid max-w-container gap-10 px-6 py-16 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20 lg:px-10 lg:py-20" data-id="element-436" data-mp="1u7yis6rdbm8x">
          <div data-id="element-437" data-mp="1z6rrllvpmp2r">
            <SectionLabel data-id="element-438" data-mp="247fj08nup7xi">Questions</SectionLabel>
            <h2 className="mt-6 text-2xl font-semibold leading-tight tracking-tight text-ink-900 lg:text-3xl" data-id="element-439" data-mp="247782mbagcoy">
              What landlords ask first
            </h2>
          </div>
          <dl className="divide-y divide-ink-100 border-t border-ink-200" data-id="element-440" data-mp="9uhbtxxg6ree">
            {FAQS.map(faq => <div key={faq.q} className="py-6" data-id="element-441" data-mp="2n5irwke5le6t">
                <dt className="text-base font-semibold tracking-tight text-ink-900" data-id="element-442" data-mp="2idhjj9k3w7m2">
                  {faq.q}
                </dt>
                <dd className="mt-2 max-w-2xl text-sm leading-relaxed text-ink-600" data-id="element-443" data-mp="2ecp8carobsn5">
                  {faq.a}
                </dd>
              </div>)}
          </dl>
        </div>
      </section>

      <CTABand title="See PropFlow running on your own portfolio." text="A 30-minute walkthrough with your properties loaded in. If it does not save you time, we will tell you so." primaryLabel="Book a demo" secondaryLabel="See all products" secondaryTo="/products" data-id="element-444" data-mp="361io67e5z3w7" />
    </>;
}